import { ProfileDto } from '@diritto/common/account/dtos/profile.dto';
import { Color } from '@diritto/common/style/color';
import { FontWeight, Typography } from '@diritto/common/style/typography';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useQueryClient } from '@tanstack/react-query';
import { ProfileMenuCollapseItem } from 'app-components/accounts/profiles/menu/ProfileMenuCollapseItem';
import { ProfileMenuItem } from 'app-components/accounts/profiles/menu/ProfileMenuItem';
import { QueryKey } from 'constants/reactQuery';
import { useAuthState } from 'contexts/AuthProvider';
import { AddProfileIcon } from 'icons/AddProfileIcon';
import { useGetInboxProfileNotiUnreadCountQuery } from 'queries/noti/useGetInboxProfileNotiUnreadCountQuery';
import { useGetMyProfilesQuery } from 'queries/profile/useGetMyProfilesQuery';
import { useCallback, useMemo, useState } from 'react';
import { Link } from 'ui-components/common/Link';

type ProfileMenuProps = {
  showInboxUnread?: boolean;
  onBlur?: () => void;
  onProfileChanged?: (profile: ProfileDto) => void;
};

export const ProfileMenu = ({ showInboxUnread = true, onBlur, onProfileChanged }: ProfileMenuProps) => {
  const queryClient = useQueryClient();
  const { profile, changeProfile, partner } = useAuthState();
  const [isOpen, setOpen] = useState<boolean>(false);
  const { data: profileData } = useGetMyProfilesQuery();
  const { data: inboxUnreadCountData } = useGetInboxProfileNotiUnreadCountQuery();

  const handleProfileAvatarClick = useCallback(
    async (target: ProfileDto) => {
      onBlur?.();
      await changeProfile(target.id);
      onProfileChanged?.(target);
    },
    [changeProfile, onBlur, onProfileChanged],
  );

  const renderCollapseButton = useCallback(() => {
    const nickname = partner && profile ? `${partner.name}(${profile?.nickname})` : profile?.nickname;
    const file = partner ? partner.profileImage : profile?.profileImage;
    return (
      <>
        <ProfileMenuCollapseItem
          nickname={nickname}
          file={file}
          direction={isOpen ? 'up' : 'down'}
          onClick={() => {
            setOpen(prev => !prev);
            queryClient.invalidateQueries({ queryKey: [QueryKey.Profile] }).then();
          }}
        />
        <StyledDivider />
      </>
    );
  }, [queryClient, isOpen, profile, partner]);

  return (
    <>
      {renderCollapseButton()}
      <StyledHidden hidden={!isOpen}>
        {profileData && profile && (
          <ProfileMenuView
            profile={profile}
            profiles={profileData.profiles}
            inboxUnreadCount={(showInboxUnread && inboxUnreadCountData?.inboxProfileUnreadCount) || {}}
            onClick={handleProfileAvatarClick}
          />
        )}
        <StyledDivider />
      </StyledHidden>
    </>
  );
};

export const ProfileMenuView = ({
  profiles,
  profile,
  inboxUnreadCount,
  onClick,
}: {
  profile: ProfileDto;
  profiles: ProfileDto[];
  inboxUnreadCount: Record<number, number>;
  onClick: (p: ProfileDto) => void;
}) => {
  const otherProfiles = useMemo(() => profiles.filter(it => it.id !== profile.id), [profile, profiles]);
  const length = useMemo(() => profiles.length, [profiles.length]);

  return (
    <>
      <StyledHidden hidden={length === 1}>
        <StyledCollapseTitle>
          <p>다른 프로필</p>
        </StyledCollapseTitle>
        {otherProfiles.map(p => (
          <ProfileMenuItem
            key={p.id}
            onClick={() => onClick(p)}
            nickname={p.nickname}
            size={24}
            file={p.profileImage}
            inboxCount={inboxUnreadCount[p.id] || 0}
          />
        ))}
      </StyledHidden>
      <StyledHidden hidden={length === 5}>
        <Link href="/accounts/profiles/add">
          <StyledAddProfile>
            <StyledIconWrapper>
              <AddProfileIcon />
            </StyledIconWrapper>
            <p>새 프로필 만들기</p>
          </StyledAddProfile>
        </Link>
      </StyledHidden>
    </>
  );
};

const StyledMenuItem = styled.div`
  display: flex;
  align-items: center;
  vertical-align: center;
  position: relative;
  width: 100%;
  height: 48px;
  padding: 12px 16px;
  text-align: left;
  cursor: pointer;
`;

const StyledAddProfile = styled(StyledMenuItem)`
  & p {
    padding: 8px;
    ${Typography.XXSmall};
  }

  &:hover {
    background-color: ${Color.Primary100};
  }
`;

const StyledCollapseTitle = styled(StyledMenuItem)`
  cursor: initial;

  & p {
    padding: 0;
    color: ${Color.Black};
    font-weight: ${FontWeight.Bold};
    ${Typography.XXSmall};
  }
`;

const StyledHidden = styled.div(
  ({ hidden }: { hidden: boolean }) => css`
    height: ${hidden ? 0 : 'initial'};
    overflow: hidden;
    transition: 0.4s height;
  `,
);

const StyledIconWrapper = styled.span`
  display: flex;
  width: 24px;
  height: 24px;
`;

const StyledDivider = styled.div`
  height: 1px;
  background-color: ${Color.Gray200};
  flex: none;
  order: 1;
  flex-grow: 0;
`;
